import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dispatchAPI } from "../services/api";
import Select from "react-select";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../styles/SwitchToggle.css";
import "../styles/forms/index.css"; // Import the modern styles
import DummyDataButton from "../components/shared/DummyDataButton.jsx";
import { extractDetailFromError } from "../utils/errorHandler.js";
import { validate as validateEmail } from "email-validator";
import {
   ERR_MISSING_CONVERSATION_ID,
   ERR_EMAIL_INVALID,
   ERR_DISPATCH_PHONE_INVALID,
   ERR_DRIVER_PHONE_INVALID,
   ERR_SUBMIT_FAILED,
   errAdditionalDriverPhoneInvalid,
} from "../utils/errorMessages.js";

const US_STATES = [
   { value: "Alabama", label: "AL - Alabama" },
   { value: "Alaska", label: "AK - Alaska" },
   { value: "Arizona", label: "AZ - Arizona" },
   { value: "Arkansas", label: "AR - Arkansas" },
   { value: "California", label: "CA - California" },
   { value: "Colorado", label: "CO - Colorado" },
   { value: "Connecticut", label: "CT - Connecticut" },
   { value: "Delaware", label: "DE - Delaware" },
   { value: "Florida", label: "FL - Florida" },
   { value: "Georgia", label: "GA - Georgia" },
   { value: "Hawaii", label: "HI - Hawaii" },
   { value: "Idaho", label: "ID - Idaho" },
   { value: "Illinois", label: "IL - Illinois" },
   { value: "Indiana", label: "IN - Indiana" },
   { value: "Iowa", label: "IA - Iowa" },
   { value: "Kansas", label: "KS - Kansas" },
   { value: "Kentucky", label: "KY - Kentucky" },
   { value: "Louisiana", label: "LA - Louisiana" },
   { value: "Maine", label: "ME - Maine" },
   { value: "Maryland", label: "MD - Maryland" },
   { value: "Massachusetts", label: "MA - Massachusetts" },
   { value: "Michigan", label: "MI - Michigan" },
   { value: "Minnesota", label: "MN - Minnesota" },
   { value: "Mississippi", label: "MS - Mississippi" },
   { value: "Missouri", label: "MO - Missouri" },
   { value: "Montana", label: "MT - Montana" },
   { value: "Nebraska", label: "NE - Nebraska" },
   { value: "Nevada", label: "NV - Nevada" },
   { value: "New Hampshire", label: "NH - New Hampshire" },
   { value: "New Jersey", label: "NJ - New Jersey" },
   { value: "New Mexico", label: "NM - New Mexico" },
   { value: "New York", label: "NY - New York" },
   { value: "North Carolina", label: "NC - North Carolina" },
   { value: "North Dakota", label: "ND - North Dakota" },
   { value: "Ohio", label: "OH - Ohio" },
   { value: "Oklahoma", label: "OK - Oklahoma" },
   { value: "Oregon", label: "OR - Oregon" },
   { value: "Pennsylvania", label: "PA - Pennsylvania" },
   { value: "Rhode Island", label: "RI - Rhode Island" },
   { value: "South Carolina", label: "SC - South Carolina" },
   { value: "South Dakota", label: "SD - South Dakota" },
   { value: "Tennessee", label: "TN - Tennessee" },
   { value: "Texas", label: "TX - Texas" },
   { value: "Utah", label: "UT - Utah" },
   { value: "Vermont", label: "VT - Vermont" },
   { value: "Virginia", label: "VA - Virginia" },
   { value: "Washington", label: "WA - Washington" },
   { value: "West Virginia", label: "WV - West Virginia" },
   { value: "Wisconsin", label: "WI - Wisconsin" },
   { value: "Wyoming", label: "WY - Wyoming" },
   { value: "Washington D.C.", label: "DC - Washington D.C." },
   // Canadian Provinces
   { value: "Alberta", label: "AB - Alberta" },
   { value: "British Columbia", label: "BC - British Columbia" },
   { value: "Manitoba", label: "MB - Manitoba" },
   { value: "New Brunswick", label: "NB - New Brunswick" },
   { value: "Newfoundland and Labrador", label: "NL - Newfoundland and Labrador" },
   { value: "Nova Scotia", label: "NS - Nova Scotia" },
   { value: "Northwest Territories", label: "NT - Northwest Territories" },
   { value: "Nunavut", label: "NU - Nunavut" },
   { value: "Ontario", label: "ON - Ontario" },
   { value: "Prince Edward Island", label: "PE - Prince Edward Island" },
   { value: "Quebec", label: "QC - Quebec" },
   { value: "Saskatchewan", label: "SK - Saskatchewan" },
   { value: "Yukon", label: "YT - Yukon" },
];

const DispatchForm = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const frontConversation = searchParams.get("frontConversation");

   const [formData, setFormData] = useState({
      dispatcherFirstName: "",
      dispatcherLastName: "",
      dispatcherEmail: "",
      afterHoursPhone: "",
      usDotNumber: "",
      mcNumber: "",
      driverFirstName: "",
      driverLastName: "",
      driverPhoneNumber: "",
      driverType: "",
      driverConsent: "",
      equipmentType: "",
      otherEquipment: "",
      truckNumber: "",
      trailerNumber: "",
      truckVin: "",
      truckLicense: "",
      truckLicenseState: "",
      driverLocation: "",
      isDriverEmpty: "",
      emptyTime: "",
      emptyLocation: "",
      enableAdditionalDrivers: false,
      frontConversation: frontConversation || "",
   });

   const [additionalDrivers, setAdditionalDrivers] = useState([]);
   const [showEmptyFields, setShowEmptyFields] = useState(false);
   const [showAdditionalDrivers, setShowAdditionalDrivers] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [error, setError] = useState("");
   const [numberType, setNumberType] = useState("usdot"); // 'usdot' or 'mc'
   const [frontConversationError, setFrontConversationError] = useState(false);

   useEffect(() => {
      // Check if frontConversation parameter is missing
      if (!frontConversation || frontConversation.trim() === "") {
         setFrontConversationError(true);
         setError(ERR_MISSING_CONVERSATION_ID);
      } else {
         setFrontConversationError(false);
         if (error.includes("Missing conversation ID")) {
            setError("");
         }
      }
   }, [frontConversation]);

   useEffect(() => {
      setShowEmptyFields(formData.isDriverEmpty === "No");
   }, [formData.isDriverEmpty]);

   useEffect(() => {
      setShowAdditionalDrivers(formData.enableAdditionalDrivers);
   }, [formData.enableAdditionalDrivers]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: type === "checkbox" ? checked : value,
      }));
   };

   // Handle filling form with dummy data
   const handleFillDummyData = (dummyData) => {
      setFormData((prev) => ({
         ...prev,
         ...dummyData,
         // Preserve frontConversation from URL
         frontConversation: frontConversation || prev.frontConversation || "",
      }));

      // Set showEmptyFields based on dummy data
      setShowEmptyFields(dummyData.isDriverEmpty === "No");
      setShowAdditionalDrivers(dummyData.enableAdditionalDrivers);
   };

   const handleDispatchPhoneChange = (value) => {
      setFormData((prev) => ({
         ...prev,
         afterHoursPhone: value || "",
      }));
   };

   const handleDriverPhoneChange = (value) => {
      setFormData((prev) => ({
         ...prev,
         driverPhoneNumber: value || "",
      }));
   };

   const handleAdditionalDriverPhoneChange = (id, value) => {
      updateAdditionalDriver(id, "phone", value || "");
   };

   const handleStateChange = (selectedOption) => {
      setFormData((prev) => ({
         ...prev,
         truckLicenseState: selectedOption ? selectedOption.value : "",
      }));
   };

   const addAdditionalDriver = () => {
      setAdditionalDrivers((prev) => [
         ...prev,
         {
            id: Date.now(),
            firstName: "",
            lastName: "",
            phone: "",
            dropLocation: "",
         },
      ]);
   };

   const removeAdditionalDriver = (id) => {
      setAdditionalDrivers((prev) => prev.filter((driver) => driver.id !== id));
   };

   const updateAdditionalDriver = (id, field, value) => {
      setAdditionalDrivers((prev) =>
         prev.map((driver) => (driver.id === id ? { ...driver, [field]: value } : driver))
      );
   };

   const validateAdditionalDrivers = () => {
      if (!showAdditionalDrivers || additionalDrivers.length === 0) {
         return true;
      }

      for (let index = 0; index < additionalDrivers.length; index++) {
         const driver = additionalDrivers[index];
         if (!driver.phone || !isValidPhoneNumber(driver.phone)) {
            setError(errAdditionalDriverPhoneInvalid(index + 1));
            return false;
         }
      }

      return true;
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError("");

      try {
         // Email validation
         if (!validateEmail(formData.dispatcherEmail)) {
            setError(ERR_EMAIL_INVALID);
            setIsSubmitting(false);
            return;
         }

         // Phone number validations
         if (!formData.afterHoursPhone || !isValidPhoneNumber(formData.afterHoursPhone)) {
            setError(ERR_DISPATCH_PHONE_INVALID);
            setIsSubmitting(false);
            return;
         }

         if (!formData.driverPhoneNumber || !isValidPhoneNumber(formData.driverPhoneNumber)) {
            setError(ERR_DRIVER_PHONE_INVALID);
            setIsSubmitting(false);
            return;
         }

         // Validate additional drivers' phone numbers when enabled
         if (!validateAdditionalDrivers()) {
            setIsSubmitting(false);
            return;
         }

         // Prepare form data with additional drivers
         const submitData = { ...formData };

         const additionalDriversData = additionalDrivers.map((driver) => {
            return {
               driverFirstName: driver.firstName,
               driverLastName: driver.lastName,
               driverPhoneNumber: driver.phone,
               driverLocation: driver.dropLocation,
            };
         });

         submitData.additionalDrivers = additionalDriversData;

         if (numberType === "mc") {
            submitData.mcNumber = `MC-${formData.mcNumber}`;
         }

         await dispatchAPI.submitForm(submitData);

         // Navigate to thank you page
         navigate("/thank-you?name=" + formData.dispatcherFirstName);
      } catch (err) {
         console.error("Error submitting form:", err);
         setError(extractDetailFromError(err) || ERR_SUBMIT_FAILED);
         setIsSubmitting(false);
      }
   };

   return (
      <div className="form-container">
         <div className="form-header-modern">
            <div className="form-header-background"></div>
            <div className="form-header-content-wrapper">
               <div className="form-header-main">
                  <div className="form-header-icon">
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                        />
                     </svg>
                  </div>
                  <div className="form-header-text">
                     <h1>Dispatch Information</h1>
                     <p>
                        Submit dispatch and driver details to initiate a new load or update driver
                        status
                     </p>
                  </div>
               </div>
            </div>
         </div>

         <form onSubmit={handleSubmit} className="form-content">
            {/* Dummy Data Button */}
            <div className="form-section" style={{ paddingBottom: "10px" }}>
               <DummyDataButton formType="dispatch" onFillData={handleFillDummyData} />
            </div>

            {/* Dispatcher Information */}
            <div className="form-section">
               <h3 className="section-title">Dispatcher Information</h3>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="dispatcherFirstName">
                        FIRST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="dispatcherFirstName"
                        name="dispatcherFirstName"
                        value={formData.dispatcherFirstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="dispatcherLastName">
                        LAST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="dispatcherLastName"
                        name="dispatcherLastName"
                        value={formData.dispatcherLastName}
                        onChange={handleInputChange}
                        placeholder="Smith"
                        required
                     />
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="dispatcherEmail">
                        EMAIL ADDRESS <span className="required">*</span>
                     </label>
                     <input
                        type="email"
                        id="dispatcherEmail"
                        name="dispatcherEmail"
                        value={formData.dispatcherEmail}
                        onChange={handleInputChange}
                        placeholder="john.smith@example.com"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="afterHoursPhone">
                        DISPATCH PHONE <span className="required">*</span>
                     </label>
                     <PhoneInput
                        id="afterHoursPhone"
                        name="afterHoursPhone"
                        value={formData.afterHoursPhone}
                        onChange={handleDispatchPhoneChange}
                        placeholder="(555) 123-4567"
                        defaultCountry="US"
                        limitMaxLength={true}
                        required
                     />
                  </div>
               </div>
            </div>

            {/* Driver Information */}
            <div className="form-section">
               <h3 className="section-title">Driver Information</h3>
               <div className="info-note">
                  <svg className="info-icon" viewBox="0 0 20 20" fill="currentColor">
                     <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                     />
                  </svg>
                  <p>
                     Driver will receive an SMS notification with dispatch details upon form
                     submission.
                  </p>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="driverFirstName">
                        FIRST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="driverFirstName"
                        name="driverFirstName"
                        value={formData.driverFirstName}
                        onChange={handleInputChange}
                        placeholder="Mike"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="driverLastName">
                        LAST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="driverLastName"
                        name="driverLastName"
                        value={formData.driverLastName}
                        onChange={handleInputChange}
                        placeholder="Johnson"
                        required
                     />
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="driverPhoneNumber">
                        CELL PHONE <span className="required">*</span>
                     </label>
                     <PhoneInput
                        id="driverPhoneNumber"
                        name="driverPhoneNumber"
                        value={formData.driverPhoneNumber}
                        onChange={handleDriverPhoneChange}
                        placeholder="(555) 987-6543"
                        defaultCountry="US"
                        limitMaxLength={true}
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="driverType">
                        DRIVER TYPE <span className="required">*</span>
                     </label>
                     <select
                        id="driverType"
                        name="driverType"
                        value={formData.driverType}
                        onChange={handleInputChange}
                        required
                     >
                        <option value="">Select Driver Type</option>
                        <option value="Solo">Solo Driver</option>
                        <option value="Team">Team Driver</option>
                     </select>
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <div className="usdot-mc-toggle-wrapper">
                        <div className="switch-toggle">
                           <input
                              type="radio"
                              id="usdotType"
                              name="numberType"
                              value="usdot"
                              checked={numberType === "usdot"}
                              onChange={(e) => setNumberType(e.target.value)}
                           />
                           <label htmlFor="usdotType">USDOT Number</label>
                           <input
                              type="radio"
                              id="mcType"
                              name="numberType"
                              value="mc"
                              checked={numberType === "mc"}
                              onChange={(e) => setNumberType(e.target.value)}
                           />
                           <label htmlFor="mcType">MC Number</label>
                        </div>
                        {numberType === "usdot" ? (
                           <div className="usdot-number-input-wrapper">
                              <span className="usdot-prefix">USDOT</span>
                              <input
                                 type="text"
                                 id="usDotNumber"
                                 name="usDotNumber"
                                 value={formData.usDotNumber}
                                 onChange={handleInputChange}
                                 placeholder="Enter Number"
                                 required
                                 className="usdot-number-input"
                              />
                           </div>
                        ) : (
                           <div className="mc-number-input-wrapper">
                              <span className="mc-prefix">MC-</span>
                              <input
                                 type="text"
                                 id="mcNumber"
                                 name="mcNumber"
                                 value={formData.mcNumber}
                                 onChange={(e) => {
                                    // Only allow numbers, max 10 digits (adjust as needed)
                                    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setFormData((prev) => ({ ...prev, mcNumber: value }));
                                 }}
                                 placeholder="Enter Number"
                                 required
                                 className="mc-number-input"
                                 inputMode="numeric"
                                 pattern="[0-9]*"
                              />
                           </div>
                        )}
                     </div>
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="driverConsent">
                        DRIVER'S CONSENT TO SMS AND TRACKING <span className="required">*</span>
                     </label>
                     <select
                        id="driverConsent"
                        name="driverConsent"
                        value={formData.driverConsent}
                        onChange={handleInputChange}
                        required
                     >
                        <option value="">Select Option</option>
                        <option value="Yes">Yes - Driver consents</option>
                        <option value="No">No - Driver does not consent</option>
                     </select>
                  </div>
               </div>
            </div>

            {/* Additional Drivers */}
            <div className="form-section">
               <h3 className="section-title">Additional Drivers</h3>
               <div className="info-note">
                  <svg className="info-icon" viewBox="0 0 20 20" fill="currentColor">
                     <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <p>
                     Add additional drivers for this dispatch. All drivers will receive SMS
                     notifications.
                  </p>
               </div>
               <div className="form-group" style={{ marginTop: "20px" }}>
                  <div className="checkbox-wrapper">
                     <input
                        type="checkbox"
                        id="enableAdditionalDrivers"
                        name="enableAdditionalDrivers"
                        checked={formData.enableAdditionalDrivers}
                        onChange={handleInputChange}
                     />
                     <label
                        htmlFor="enableAdditionalDrivers"
                        style={{
                           display: "inline",
                           textTransform: "none",
                           margin: "14px",
                        }}
                     >
                        Will other drivers be utilized for pickup or delivery?
                     </label>
                  </div>
               </div>

               {showAdditionalDrivers && (
                  <div className="additional-drivers-container">
                     <button
                        type="button"
                        className="add-driver-button"
                        onClick={addAdditionalDriver}
                     >
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           width="20"
                           height="20"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                           />
                        </svg>
                        Add Additional Driver
                     </button>

                     {additionalDrivers.map((driver, index) => (
                        <div key={driver.id} className="additional-driver-card">
                           <div className="additional-driver-header">
                              <span className="additional-driver-title">
                                 Additional Driver {index + 1}
                              </span>
                              <button
                                 type="button"
                                 className="remove-driver-button"
                                 onClick={() => removeAdditionalDriver(driver.id)}
                              >
                                 Remove Driver
                              </button>
                           </div>
                           <div className="additional-driver-form-row">
                              <div className="additional-driver-form-group">
                                 <label>
                                    FIRST NAME <span className="required">*</span>
                                 </label>
                                 <input
                                    type="text"
                                    value={driver.firstName}
                                    onChange={(e) =>
                                       updateAdditionalDriver(
                                          driver.id,
                                          "firstName",
                                          e.target.value
                                       )
                                    }
                                    placeholder="Enter first name"
                                    required
                                 />
                              </div>
                              <div className="additional-driver-form-group">
                                 <label>
                                    LAST NAME <span className="required">*</span>
                                 </label>
                                 <input
                                    type="text"
                                    value={driver.lastName}
                                    onChange={(e) =>
                                       updateAdditionalDriver(driver.id, "lastName", e.target.value)
                                    }
                                    placeholder="Enter last name"
                                    required
                                 />
                              </div>
                           </div>
                           <div className="additional-driver-form-row">
                              <div className="additional-driver-form-group">
                                 <label>
                                    CELL PHONE <span className="required">*</span>
                                 </label>
                                 <PhoneInput
                                    value={driver.phone}
                                    onChange={(value) =>
                                       handleAdditionalDriverPhoneChange(driver.id, value)
                                    }
                                    placeholder="(555) 123-4567"
                                    defaultCountry="US"
                                    limitMaxLength={true}
                                    required
                                 />
                              </div>
                              <div className="additional-driver-form-group">
                                 <label>DROP LOCATION</label>
                                 <input
                                    type="text"
                                    value={driver.dropLocation}
                                    onChange={(e) =>
                                       updateAdditionalDriver(
                                          driver.id,
                                          "dropLocation",
                                          e.target.value
                                       )
                                    }
                                    placeholder="City, State"
                                 />
                              </div>
                           </div>
                        </div>
                     ))}
                  </div>
               )}
            </div>

            {/* Equipment Information */}
            <div className="form-section">
               <h3 className="section-title">Equipment Information</h3>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="equipmentType">
                        EQUIPMENT TYPE <span className="required">*</span>
                     </label>
                     <select
                        id="equipmentType"
                        name="equipmentType"
                        value={formData.equipmentType}
                        onChange={handleInputChange}
                        required
                     >
                        <option value="">Select Equipment Type</option>
                        <option value="Dry Van">Dry Van</option>
                        <option value="Reefer">Reefer</option>
                        <option value="Flatbed">Flatbed</option>
                        <option value="Step Deck">Step Deck</option>
                        <option value="Lowboy">Lowboy</option>
                        <option value="Other">Other</option>
                     </select>
                  </div>
                  {formData.equipmentType === "Other" && (
                     <div className="form-group">
                        <label htmlFor="otherEquipment">
                           SPECIFY EQUIPMENT <span className="required">*</span>
                        </label>
                        <input
                           type="text"
                           id="otherEquipment"
                           name="otherEquipment"
                           value={formData.otherEquipment}
                           onChange={handleInputChange}
                           placeholder="Enter equipment type"
                           required
                        />
                     </div>
                  )}
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="truckNumber">
                        TRUCK NUMBER <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="truckNumber"
                        name="truckNumber"
                        value={formData.truckNumber}
                        onChange={handleInputChange}
                        placeholder="T-001"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="trailerNumber">TRAILER NUMBER</label>
                     <input
                        type="text"
                        id="trailerNumber"
                        name="trailerNumber"
                        value={formData.trailerNumber}
                        onChange={handleInputChange}
                        placeholder="TR-502 (Optional)"
                     />
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="truckVin">
                        TRUCK VIN <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="truckVin"
                        name="truckVin"
                        value={formData.truckVin}
                        onChange={handleInputChange}
                        placeholder="1HGBH41JXMN109186"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="truckLicense">
                        TRUCK LICENSE PLATE <span className="required">*</span>
                     </label>
                     <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                        <div style={{ flex: "0 0 150px" }}>
                           <Select
                              inputId="truckLicenseState"
                              required
                              options={US_STATES}
                              onChange={handleStateChange}
                              value={US_STATES.find(
                                 (state) => state.value === formData.truckLicenseState
                              )}
                              placeholder="Select State/Province"
                              isSearchable={true}
                              styles={{
                                 control: (provided, state) => ({
                                    ...provided,
                                    minHeight: "42px",
                                    border: state.isFocused
                                       ? "2px solid #3B82F6"
                                       : "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    backgroundColor: "#FFFFFF",
                                    boxShadow: state.isFocused
                                       ? "0 0 0 3px rgba(59, 130, 246, 0.1)"
                                       : "none",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                       borderColor: "#9CA3AF",
                                    },
                                 }),
                                 menu: (provided) => ({
                                    ...provided,
                                    borderRadius: "8px",
                                    border: "1px solid #E5E7EB",
                                    boxShadow:
                                       "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                    marginTop: "4px",
                                 }),
                                 menuList: (provided) => ({
                                    ...provided,
                                    padding: "4px",
                                    maxHeight: "200px",
                                 }),
                                 option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                       ? "#3B82F6"
                                       : state.isFocused
                                         ? "#F3F4F6"
                                         : "transparent",
                                    color: state.isSelected ? "#FFFFFF" : "#374151",
                                    borderRadius: "6px",
                                    margin: "2px 0",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: state.isSelected ? "500" : "400",
                                    transition: "all 0.15s ease-in-out",
                                    "&:hover": {
                                       backgroundColor: state.isSelected ? "#3B82F6" : "#E5E7EB",
                                    },
                                 }),
                                 singleValue: (provided) => ({
                                    ...provided,
                                    color: "#374151",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                 }),
                                 placeholder: (provided) => ({
                                    ...provided,
                                    color: "#9CA3AF",
                                    fontSize: "14px",
                                 }),
                                 input: (provided) => ({
                                    ...provided,
                                    color: "#374151",
                                    fontSize: "14px",
                                 }),
                                 indicatorSeparator: (provided) => ({
                                    ...provided,
                                    backgroundColor: "#E5E7EB",
                                 }),
                                 dropdownIndicator: (provided, state) => ({
                                    ...provided,
                                    color: state.isFocused ? "#3B82F6" : "#9CA3AF",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                       color: "#3B82F6",
                                    },
                                 }),
                              }}
                           />
                        </div>
                        <div style={{ flex: "1" }}>
                           <input
                              type="text"
                              id="truckLicense"
                              name="truckLicense"
                              value={formData.truckLicense}
                              onChange={handleInputChange}
                              placeholder="ABC123"
                              required
                              style={{ width: "100%" }}
                           />
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            {/* Current Status */}
            <div className="form-section">
               <h3 className="section-title">Current Status</h3>
               <div className="form-group">
                  <label htmlFor="driverLocation">
                     DRIVER'S CURRENT LOCATION <span className="required">*</span>
                  </label>
                  <input
                     type="text"
                     id="driverLocation"
                     name="driverLocation"
                     value={formData.driverLocation}
                     onChange={handleInputChange}
                     placeholder="City, State"
                     required
                  />
               </div>
               <div className="form-group">
                  <label htmlFor="isDriverEmpty">
                     IS DRIVER EMPTY NOW? <span className="required">*</span>
                  </label>
                  <select
                     id="isDriverEmpty"
                     name="isDriverEmpty"
                     value={formData.isDriverEmpty}
                     onChange={handleInputChange}
                     required
                  >
                     <option value="">Select Option</option>
                     <option value="Yes">Yes - Driver is empty</option>
                     <option value="No">No - Driver has a load</option>
                  </select>
               </div>

               {showEmptyFields && (
                  <div className="form-row">
                     <div className="form-group">
                        <label htmlFor="emptyTime">
                           ESTIMATED EMPTY TIME <span className="required">*</span>
                        </label>
                        <input
                           type="text"
                           id="emptyTime"
                           name="emptyTime"
                           value={formData.emptyTime}
                           onChange={handleInputChange}
                           placeholder="e.g., 2:30 PM EST"
                           required={showEmptyFields}
                        />
                        <p className="helper-text">e.g., 2:30 PM EST</p>
                     </div>
                     <div className="form-group">
                        <label htmlFor="emptyLocation">
                           EMPTY LOCATION <span className="required">*</span>
                        </label>
                        <input
                           type="text"
                           id="emptyLocation"
                           name="emptyLocation"
                           value={formData.emptyLocation}
                           onChange={handleInputChange}
                           placeholder="Where will the driver be empty?"
                           required={showEmptyFields}
                        />
                     </div>
                  </div>
               )}
            </div>

            {frontConversation && (
               <input type="hidden" name="frontConversation" value={frontConversation} />
            )}

            <div className="form-section" style={{ paddingTop: 0 }}>
               {error && (
                  <div className="error-message-box">
                     <div className="error-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                           />
                        </svg>
                     </div>
                     <div className="error-content">
                        <h4>Error</h4>
                        <p>{error}</p>
                     </div>
                  </div>
               )}

               <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || frontConversationError}
               >
                  {isSubmitting ? (
                     <div className="auth-loading">
                        <div className="auth-spinner"></div>
                        Submitting...
                     </div>
                  ) : (
                     "Submit Dispatch Information"
                  )}
               </button>
            </div>
         </form>
      </div>
   );
};

export default DispatchForm;
